import { useMarket } from '@ecosystem/market-context-selection';
import { useAnalytics, useAuthentication, useFeedback } from '@ecosystem/newron-client-lib';
import React, { ReactElement, Suspense, lazy, useCallback, useContext, useEffect, useRef } from 'react';

import { NotificationToastType } from '../../../common';
import { UpsellContext } from '../../../state';
import { setIsModalOpen, setNotificationMessage } from '../../../state/action';
import { waitForElement } from '../../../utils';
import { getMessageCode } from '../../consumer-slides-message/utils/getMessageCode';

const FirstImage = lazy(() => import('./images/FirstImage'));
const SecondImage = lazy(() => import('./images/SecondImage'));
const ThirdImage = lazy(() => import('./images/ThirdImage'));
const FourthImage = lazy(() => import('./images/FourthImage'));

const QualtricsFeedbackIdentifier = 'ConsumerSlidesQualtricsFeedbackIdentifier';
const QualtricsButtonsSelector = '.QSI__EmbeddedFeedbackContainer_TextButton';

export const ConsumerSlide = ({
  children,
  slideNumber,
}: {
  children: ReactElement;
  slideNumber: number;
}): ReactElement => {
  const {
    dispatch,
    state: { isModalOpen, pageName, bannerNumber, activeSlideIndex },
  } = useContext(UpsellContext);
  const { extendEmbeddedData } = useFeedback();
  const { user } = useAuthentication();
  const { market } = useMarket();
  const { track } = useAnalytics();
  const bannerId = useRef<number | undefined>(bannerNumber || undefined);

  useEffect(() => {
    if (bannerNumber !== bannerId.current) {
      bannerId.current = bannerNumber;
    }
  }, [bannerNumber]);

  const eventClick = useCallback(
    button => {
      if (button?.title === 'Yes, I am interested') {
        track('upsell-lead-generated', {
          pageName,
          bannerName: getMessageCode(bannerId.current),
          slideNumber,
        });
        dispatch(
          setNotificationMessage({
            message: 'Thank you for your request. We will be in touch with more information.',
            type: NotificationToastType.Success,
            timeout: 3000,
          })
        );
      } else {
        track('upsell-lead-not-generated', {
          pageName,
          bannerName: getMessageCode(bannerId.current),
          slideNumber,
        });
      }
      dispatch(setIsModalOpen(false));
    },
    // eslint-disable-next-line
    [dispatch, track]
  );

  const handleClick = useCallback(async () => {
    const elementExists = await waitForElement(QualtricsButtonsSelector);
    if (elementExists) {
      const buttons = document.querySelectorAll(QualtricsButtonsSelector);
      buttons.forEach((button: HTMLElement) => {
        if (button?.title === 'No, Thanks') {
          button.style.display = 'none';
        }
        button.onclick = () => {
          eventClick(button);
        };
      });
    }
  }, [eventClick]);

  useEffect(() => {
    if (isModalOpen && activeSlideIndex === slideNumber - 1) {
      extendEmbeddedData({
        market: `${market?.itemGroupId}/${market?.countryId}`,
        country: user?.country,
      });
      handleClick();
    }
  }, [extendEmbeddedData, handleClick, isModalOpen, market, user?.country, activeSlideIndex, slideNumber]);
  return (
    <div>
      <div className="consumer-stories-slide flex justify-center items-center" data-test-id="consumer-stories-slide">
        {children}
      </div>
      {activeSlideIndex === slideNumber - 1 && (
        <>
          <p className="consumer-stories-slide-text text-center text-md mt-2">
            Register your interest today and your GfK Account Manager will be in touch with more information.
          </p>
          <div
            className="consumer-stories-slide-buttons flex justify-evenly"
            id={QualtricsFeedbackIdentifier}
            data-test-id="qualtrics-buttons"
          />
        </>
      )}
    </div>
  );
};

export const ConsumerSlidesFirst = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <ConsumerSlide slideNumber={1}>
        <div className="consumer-stories-slide-image-wrapper p-1 rounded-2">
          <FirstImage />
        </div>
      </ConsumerSlide>
    </Suspense>
  );
};

export const ConsumerSlidesSecond = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <ConsumerSlide slideNumber={2}>
        <div className="consumer-stories-slide-image-wrapper p-1 rounded-2">
          <SecondImage />
        </div>
      </ConsumerSlide>
    </Suspense>
  );
};

export const ConsumerSlidesThird = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <ConsumerSlide slideNumber={3}>
        <div className="consumer-stories-slide-image-wrapper p-1 rounded-2">
          <ThirdImage />
        </div>
      </ConsumerSlide>
    </Suspense>
  );
};

export const ConsumerSlidesFourth = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <ConsumerSlide slideNumber={4}>
        <div className="consumer-stories-slide-image-wrapper p-1 rounded-2">
          <FourthImage />
        </div>
      </ConsumerSlide>
    </Suspense>
  );
};

export const ConsumerFirstSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="consumer-stories-slide-header" className="text-2xl">
      Unlock the power of lost shopper insights with Stories on <span className="text-brand">gfk</span>newron Consumer
    </span>
  );
};

export const ConsumerSecondSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="consumer-stories-slide-header" className="text-2xl">
      Quantify the size and value of your lost shoppers
    </span>
  );
};

export const ConsumerThirdSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="consumer-stories-slide-header" className="text-2xl">
      Gain deeper understanding on why shoppers buy competitor brands
    </span>
  );
};

export const ConsumerFourthSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="consumer-stories-slide-header" className="text-2xl">
      Learn how to better target shoppers in future and convert them to your brand
    </span>
  );
};

export const ConsumerQualtricsSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="consumer-stories-slide-header" className="text-2xl">
      How do you find out more?
    </span>
  );
};
